<template>
  <div>
    <div class="card-group" v-if="page">
      <h2 class="col-12">{{ $t("shop-title") }}</h2>

      <div v-if="page.doc && page.doc.content" class="row mb-5">
        <page-title
          class="col-12 w-100 d-block"
          :subheading="page.doc.content"
        />
      </div>

      <div class="row w-100">
        <!-- Zanka gre direktno čez elemente, ki ustrezajo pogoju in imajo nastavljeno sliko -->
        <div
          v-for="item in visibleShopItems"
          :key="item._id"
          class="col-12 col-sm-6 col-md-12 col-lg-6 col-xl-3"
          v-show="item.show"
        >
          <div class="card mb-3">
            <div class="card-header card-header-small">
              {{ $t(`${item.title}`) }}
            </div>

            <div class="card-body">
              <a class="text-center">
                <img
                  :src="item.image"
                  :alt="item.alt"
                  class="card-img internal-video"
                />
              </a>

              <div class="mt-3">
                <p v-if="hasActivePrice(item)">
                  {{
                    $t("shop-price", [
                      formatDate(item.currentTicketPricePeriod.from),
                      formatDate(item.currentTicketPricePeriod.to),
                    ])
                  }}
                </p>
                <p v-else>{{ $t(`${item.translations.notForSale}`) }}</p>
                <br />
                <span class="bold">
                  {{ item.currentTicketPricePeriod.price }} € /
                  {{ calculateHtl(item.currentTicketPricePeriod.price) }} HTL
                </span>
              </div>

              <table style="width: 100%">
                <tr>
                  <td v-if="item.canBuy" style="width: 50%">
                    <buy-tickets-modal
                      :buttonText="item.translations.buyText"
                      :buyQuestion="item.translations.buyQuestion"
                      :buyConfirm="item.translations.buyConfirm"
                      :buyCount="item.translations.buyCount"
                      :price="item.currentTicketPricePeriod.price"
                      :disabled="!item.active"
                      :shopItemTxtId="item.txtId"
                      :ticketsCollected="userTicketsCollected(item)"
                    />
                  </td>
                  <td v-if="item.canCollect" style="width: 50%">
                    <collect-packages-modal
                      :buttonText="$t(item.translations.collectText)"
                      :uid="page.viewer.id"
                      :disabled="!item.active"
                      :shopItemTxtId="item.txtId"
                      :packagesBought="userPackagesBought(item)"
                      :packagesCollected="userPackagesCollected(item)"
                      :qrInstructions="$t(item.translations.qrInstructions)"
                    />
                  </td>

                  <td v-if="item.canSend" style="width: 50%">
                    <send-tickets-modal
                      :buttonText="$t(item.translations.sendText)"
                      :price="item.currentTicketPricePeriod.price"
                      :disabled="!item.active || userTicketsBought(item) === 0"
                      :shopItemTxtId="item.txtId"
                      :ticketsBought="userTicketsBought(item)"
                      :downline="page.viewer.downline"
                      :upline="page.viewer.upline"
                    />
                  </td>
                </tr>
              </table>

              <div class="mt-3">
                {{ $t("shop-message") }}<br />
                <hr />
                <div style="width: 100%">
                  {{ $t(`${item.translations.boughtText}`) }}:
                  <span class="bold">{{
                    userTicketsBought(item) || userPackagesBought(item)
                  }}</span
                  >.
                </div>

                <div v-show="item.canCollect" style="width: 100%">
                  {{ $t(`${item.translations.collectedText}`) }}:
                  <span class="bold">{{
                    userTicketsCollected(item) || userPackagesCollected(item)
                  }}</span
                  >.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import gql from "graphql-tag";
import { mapGetters } from "vuex";
import BuyTicketsModal from "@/components/modals/BuyTicketsModal";
import SendTicketsModal from "@/components/modals/SendTicketsModal";
import CollectPackagesModal from "@/components/modals/CollectPackagesModal";
import PageTitle from "@/components/common/PageTitle";

//import winter26Img from "@/assets/images/SEMINAR_WINTER_2026.jpg";
//import summer26Img from "@/assets/images/SEMINAR_SUMMER_2026.jpg";
import autumn26Img from "@/assets/images/SEMINAR_AUTUMN_2026.jpg";
import winter27Img from "@/assets/images/SEMINAR_WINTER_2027.jpg";
import startPackImg from "@/assets/images/STARTPACK.jpeg";

// Zemljevid slik in opisov glede na txtId
const seminarAssets = {
  //tw26: { image: winter26Img, alt: "Winter seminar 2026" },
  //ts26: { image: summer26Img, alt: "Summer seminar 2026" },
  ta26: { image: autumn26Img, alt: "Autumn seminar 2026" },
  tw27: { image: winter27Img, alt: "Winter seminar 2027" },
  pst1: { image: startPackImg, alt: "Start Pack" },
};

export default {
  name: "MerchantPage",
  components: {
    PageTitle,
    BuyTicketsModal,
    SendTicketsModal,
    CollectPackagesModal,
  },
  data() {
    return {
      page: null,
    };
  },
  computed: {
    ...mapGetters({
      htlEur: "prices/htlEur",
    }),
    // Računalna lastnost, ki vzame shopItems iz grafa in jim doda ustrezno sliko ter alt
    visibleShopItems() {
      if (!this.page || !this.page.shopItems) return [];

      return this.page.shopItems
        .filter((item) => seminarAssets[item.txtId]) // Vzame samo tiste, ki jih poznamo
        .map((item) => ({
          ...item,
          image: seminarAssets[item.txtId].image,
          alt: seminarAssets[item.txtId].alt,
        }));
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return "";
      // Tukaj uporabi isto logiko, kot jo imaš v svojem filtru formatDateShort2
      // Npr. klic obstoječe funkcije ali knjižnice:
      return this.$options.filters.formatDateShort2(date);
      // Ali če imaš funkcijo definirano zunaj: return formatDateShort2(date);
    },
    hasActivePrice(item) {
      return (
        item.currentTicketPricePeriod != null &&
        item.currentTicketPricePeriod.to != null
      );
    },
    calculateHtl(price) {
      return this.htlEur ? (price / this.htlEur).toFixed(2) : "0.00";
    },
    userTicketsBought(shopItem) {
      if (!this.page || !this.page.viewer || !this.page.viewer.totals) return 0;

      switch (shopItem.txtId) {
        // case "tw26":
        //   return this.page.viewer.totals.ticketWinterTwentySix || 0;
        // case "ts26":
        //   return this.page.viewer.totals.ticketSummerTwentySix || 0;
        case "ta26":
          return this.page.viewer.totals.ticketAutumnTwentySix || 0;
        case "tw27":
          return this.page.viewer.totals.ticketWinterTwentySeven || 0;
        default:
          return 0;
      }
    },

    userTicketsCollected(shopItem) {
      if (!this.page || !this.page.viewer || !this.page.viewer.totals) return 0;

      switch (shopItem.txtId) {
        case "ta26":
          return this.page.viewer.totals.ticketAutumnTwentySixCollected || 0;
        case "tw27":
          return this.page.viewer.totals.ticketWinterTwentySevenCollected || 0;
        default:
          return 0;
      }
    },
    userPackagesBought(shopItem) {
      if (!this.page || !this.page.viewer || !this.page.viewer.totals) return 0;

      switch (shopItem.txtId) {
        case "pst1":
          return this.page.viewer.totals.starterPackages || 0;
        default:
          return 0;
      }
    },
    userPackagesCollected(shopItem) {
      if (!this.page || !this.page.viewer || !this.page.viewer.totals) return 0;

      switch (shopItem.txtId) {
        case "pst1":
          return this.page.viewer.totals.starterPackagesCollected || 0;
        default:
          return 0;
      }
    },
  },
  apollo: {
    page: {
      query: gql`
        query page {
          shopItems {
            _id
            txtId
            active
            title
            content
            show
            canBuy
            canSend
            canCollect
            translations {
              buyText
              boughtText
              collectText
              collectedText
              sendText
              notForSale
              buyQuestion
              buyCount
              buyConfirm
              qrInstructions
            }
            currentTicketPricePeriod {
              from
              to
              price
            }
            ticketPricePeriods {
              from
              to
              price
            }
          }
          serverTime {
            now
          }
          doc(id: "shop") {
            content
            title
          }
          ticketPrice {
            price
            until
          }
          viewer {
            id
            totals {
              starterPackages
              starterPackagesCollected
              # ticketWinterTwentySix
              # ticketSummerTwentySix
              ticketAutumnTwentySix
              ticketAutumnTwentySixCollected
              ticketWinterTwentySeven
              ticketWinterTwentySevenCollected
              tokens
            }
          }
          viewerIsAdmin
        }
      `,
      update: (data) => data,
      result({ data }) {
        if (data) {
          this.$store.commit("user/updateAvailable", {
            tokens: data.viewer.totals.tokens,
          });
          if (data.viewerIsAdmin) {
            this.$store.commit("user/setAdmin");
          }
        }
      },
    },
  },
};
</script>

<style scoped>
.card-m-small {
  height: 2rem;
}

.bold {
  font-weight: 750;
}
</style>